import {
  accessibleBusinesses,
  canAccessClient,
  canAccessInvoice,
  canManageOrganization,
  hasOrganizationAccess,
} from "../accessControl";
import { withClientFinancials } from "../clientFinancials";

export const PROTOTYPE_WORKSPACE_REPOSITORY_CAPABILITIES = Object.freeze({
  mode: "prototype",
  productionReady: false,
  authority: "in-memory-records",
  organizationScope: true,
  managedBusinessScope: true,
  serverEnforced: false,
});

const emptyView = Object.freeze({
  organization: null,
  businesses: [],
  clients: [],
  services: [],
  invoices: [],
  activeInvoices: [],
  intakeItems: [],
  documentExpectations: [],
  bankTransactions: [],
  quickBooksExports: [],
  communications: [],
  workReminders: [],
  workTasks: [],
  workItemActivities: [],
  activityLog: [],
});

const belongsToOrganization = (record, organizationId, organizationBusinessIds) => {
  if (!record) return false;
  if (record.workspaceId) return record.workspaceId === organizationId;
  return Boolean(record.businessId && organizationBusinessIds.has(record.businessId));
};

export const createScopedWorkspaceView = ({ organizationId, profile, records = {} }) => {
  const organization = (records.workspaces || []).find((item) => item.id === organizationId) || null;
  if (!organization || !hasOrganizationAccess(profile) || (profile.workspaceId && profile.workspaceId !== organizationId)) return emptyView;

  const organizationBusinessRecords = (records.businesses || []).filter((business) => business.workspaceId === organizationId);
  const organizationBusinessIds = new Set(organizationBusinessRecords.map((business) => business.id));
  const businesses = accessibleBusinesses(organizationBusinessRecords, profile);
  const accessibleBusinessIds = new Set(businesses.map((business) => business.id));
  const inAccessibleBusiness = (record) => belongsToOrganization(record, organizationId, organizationBusinessIds)
    && accessibleBusinessIds.has(record.businessId);

  const accessibleClients = (records.clients || []).filter((client) => client.workspaceId === organizationId && canAccessClient(profile, client));
  const services = (records.services || []).filter((service) => service.workspaceId === organizationId);
  const invoices = (records.invoices || [])
    .filter((invoice) => invoice.workspaceId === organizationId && canAccessInvoice(profile, invoice))
    .map((invoice) => ({
      ...invoice,
      status: records.invoiceStatuses?.[invoice.id] || invoice.status,
      archived: (records.archivedInvoiceIds || []).includes(invoice.id),
    }));
  const clients = accessibleClients.map((client) => withClientFinancials(client, invoices));
  const intakeItems = (records.intakeItems || []).filter(inAccessibleBusiness);
  const documentExpectations = (records.documentExpectations || []).filter(inAccessibleBusiness);
  const bankTransactions = (records.bankTransactions || []).filter(inAccessibleBusiness);
  const quickBooksExports = (records.quickBooksExports || []).filter(inAccessibleBusiness);
  const communications = (records.communications || []).filter((item) => belongsToOrganization(item, organizationId, organizationBusinessIds)
    && (!item.businessId || accessibleBusinessIds.has(item.businessId)));
  const workReminders = (records.workReminders || []).filter(inAccessibleBusiness);
  const workTasks = (records.workTasks || []).filter(inAccessibleBusiness);
  const visibleWorkIds = new Set([
    ...workReminders.map((item) => item.id),
    ...workTasks.map((item) => item.id),
  ]);
  const workItemActivities = (records.workItemActivities || []).filter((item) => inAccessibleBusiness(item)
    && visibleWorkIds.has(item.taskId || item.reminderId));

  const entityVisible = (entry) => {
    if (entry.workspaceId && entry.workspaceId !== organizationId) return false;
    if (entry.entityType === "organization" || entry.entityType === "data") return entry.entityId === organizationId;
    if (entry.entityType === "business") return accessibleBusinessIds.has(entry.entityId);
    if (entry.entityType === "invoice") return invoices.some((invoice) => invoice.id === entry.entityId);
    if (entry.entityType === "client") return clients.some((client) => client.id === entry.entityId);
    if (entry.entityType === "intake") return intakeItems.some((item) => item.id === entry.entityId);
    if (entry.entityType === "bank-transaction") return bankTransactions.some((item) => item.id === entry.entityId);
    if (entry.entityType === "quickbooks-export") return quickBooksExports.some((item) => item.id === entry.entityId);
    if (entry.entityType === "communication") return communications.some((item) => item.id === entry.entityId);
    if (entry.entityType === "work-task") return workTasks.some((item) => item.id === entry.entityId);
    if (entry.entityType === "work-reminder") return workReminders.some((item) => item.id === entry.entityId);
    if (entry.entityType === "team-member") {
      const member = (records.teamMembers || []).find((item) => item.id === entry.entityId && item.workspaceId === organizationId);
      return Boolean(member && (canManageOrganization(profile) || member.id === profile.id));
    }
    if (entry.entityType === "profile") return entry.entityId === profile.id;
    return Boolean(entry.workspaceId === organizationId && canManageOrganization(profile));
  };
  const activityLog = (records.activityLog || []).filter(entityVisible);

  return {
    organization,
    businesses,
    clients,
    services,
    invoices,
    activeInvoices: invoices.filter((invoice) => !invoice.archived),
    intakeItems,
    documentExpectations,
    bankTransactions,
    quickBooksExports,
    communications,
    workReminders,
    workTasks,
    workItemActivities,
    activityLog,
  };
};

export const scopedWorkspaceRepository = Object.freeze({
  capabilities: PROTOTYPE_WORKSPACE_REPOSITORY_CAPABILITIES,
  read: createScopedWorkspaceView,
});
